import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Theme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import Field from "components/Field";
import SelectEntity from "components/SelectEntity";
import arrayMutators from "final-form-arrays";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useCallback, useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  IProduct,
  ISaveProduct,
  ListResponseOfProductType,
} from "services/KnowfullyAdminClient";
import { CommonFormProps } from "types/common-types";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

type ProductFormValues = Omit<IProduct, "productType" | "skuIds"> &
  CommonFormProps<IProduct, ISaveProduct> &
  FormProps<ISaveProduct>;

const { Form } = withTypes<ProductFormValues>();

const ProductForm = ({
  product,
  submitLabel,
  onCancel,
  onSubmit,
}: ProductFormValues) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { productService } = useGlobalContext();

  const isEditing = !!product?.id;

  const fetchedProductTypes = useQuery<ListResponseOfProductType>(
    "productTypes",
    () =>
      productService.getProductTypes(
        new AdminListRequestOfBaseFilter({ sortField: "name" })
      )
  );

  const init = useMemo(
    () =>
      pick(product ?? {}, [
        "name",
        "description",
        "title",
        "productTypeId",
        "urlPath",
        "crmId",
        "ownerOrganizationId",
      ]),
    [product]
  );

  const innerOnSubmit = useCallback(
    (values: ProductFormValues) => {
      return onSubmit(values);
    },
    [onSubmit]
  );

  return (
    <Form
      onSubmit={innerOnSubmit}
      initialValues={init}
      mutators={{ ...arrayMutators }}
      render={({
        form: { reset, change },
        handleSubmit,
        submitting,
        dirty,
        submitError,
        values,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  mode={
                    isEditing
                      ? ContextOrganizationFieldMode.EditDisabled
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                  label="ownerOrganization"
                  formChange={change}
                  organizationFieldName="ownerOrganizationId"
                />

                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="nameText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="title" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "productTitleLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="titleText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="description">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "descriptionLabel" })}
                        InputLabelProps={{ required: false }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="descriptionText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="urlPath" isUrl>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "productUrlPathLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="urlText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="productTypeId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          fetchedData={fetchedProductTypes}
                          labelField="name"
                          label={t({ id: "typeLabel" })}
                          required={true}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {isEditing && (
                  <Field name="crmId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          disabled
                          label={t({ id: "crmIdLabel" }) || "CRM ID"}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>
                )}

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                disabled={submitting || !dirty}
                onClick={() => {
                  reset();
                  onCancel && onCancel();
                }}
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={submitting || !dirty}
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        );
      }}
    />
  );
};

export default ProductForm;
